import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ContactBanner.css";
import RichTextContent, { CmsText } from "../../../RichTextContent/RichTextContent";
import { useContactSection } from "../../../../context/ContactPageContext";
import {
  getContactInfoColumnsFromSection,
  getContactLocationsFromSection,
} from "../../../../services/contentApi";
import { resolveMediaUrl } from "../../../../utils/resolveMediaUrl";
import { stripRichText } from "../../../../utils/richText";

function toMapPosition(value) {
  return stripRichText(value);
}

function parseMapPercent(value) {
  const parsed = parseFloat(String(toMapPosition(value)).replace("%", "").trim());
  return Number.isFinite(parsed) ? parsed : 50;
}

function getTooltipPlacement(loc) {
  const x = parseMapPercent(loc.x);
  const y = parseMapPercent(loc.y);

  if (x >= 58) return "left";
  if (y <= 38) return "below";
  return "above";
}

const ContactBanner = () => {
  const { section, loading } = useContactSection("contact-banner");
  const [active, setActive] = useState(null);
  const isTouchRef = useRef(false);

  useEffect(() => {
    function markTouch() {
      isTouchRef.current = true;
    }

    window.addEventListener("touchstart", markTouch, {
      passive: true,
      once: true,
    });
    return () => window.removeEventListener("touchstart", markTouch);
  }, []);

  const locations = useMemo(
    () => (section?.fields ? getContactLocationsFromSection(section) : []),
    [section]
  );

  const infoColumns = useMemo(
    () => (section?.fields ? getContactInfoColumnsFromSection(section) : []),
    [section]
  );

  const mapSrc = useMemo(() => {
    const url = section?.fields?.map_image;
    return url ? resolveMediaUrl(url) : "";
  }, [section]);

  if (loading || !section?.fields) return null;

  const fields = section.fields;

  return (
    <section className="contact">
      <div className="contact__container">
        <div className="contact__header">
          {fields.tag ? (
            <CmsText content={fields.tag} as="span" />
          ) : null}
          {fields.title ? (
            <CmsText content={fields.title} as="h2" />
          ) : null}
          {fields.description ? (
            <RichTextContent content={fields.description} />
          ) : null}
        </div>

        {mapSrc ? (
          <div className="contact__map">
            <img src={mapSrc} alt="" />

            {locations.map((loc) => {
              const isActive = active === loc.id;
              const placement = getTooltipPlacement(loc);
              const name = toMapPosition(loc.name);
              const address = toMapPosition(loc.address);
              const code = toMapPosition(loc.code);

              return (
                <div
                  key={loc.id}
                  className={`dotcon${isActive ? " dotcon--active" : ""}`}
                  style={{ top: toMapPosition(loc.y), left: toMapPosition(loc.x) }}
                  onMouseEnter={() => {
                    if (!isTouchRef.current) setActive(loc.id);
                  }}
                  onMouseLeave={() => {
                    if (!isTouchRef.current) setActive(null);
                  }}
                  onClick={() =>
                    setActive((current) => (current === loc.id ? null : loc.id))
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setActive((current) =>
                        current === loc.id ? null : loc.id
                      );
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isActive}
                  aria-label={
                    loc.name
                      ? `Show ${stripRichText(loc.name)} office details`
                      : "Show office details"
                  }
                >
                  <span />

                  {isActive ? (
                    <div
                      className={`contact-map-tooltip contact-map-tooltip--${placement}`}
                      role="tooltip"
                    >
                      {name ? (
                        <p className="contact-map-tooltip__title">{name}</p>
                      ) : null}
                      {address ? (
                        <p className="contact-map-tooltip__address">{address}</p>
                      ) : null}
                      {code ? (
                        <p className="contact-map-tooltip__code">{code}</p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}

        {infoColumns.length > 0 ? (
          <div className="contact__info">
            {infoColumns.map((column) => (
              <div key={column.title || column.value}>
                {column.title ? (
                  <CmsText
                    content={column.title}
                    as="h4"
                    className="contact__info-title"
                  />
                ) : null}
                {column.description ? (
                  <RichTextContent
                    content={column.description}
                    className="contact__info-description"
                  />
                ) : null}
                {column.value ? (
                  <CmsText
                    content={column.value}
                    as="span"
                    className="contact__info-value"
                  />
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default ContactBanner;
